import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import type { Workspace, Project, User, Task } from '@/types'
import { toast } from 'sonner'

interface UseNodeFormDataProps {
  /** The form configuration to determine what data to load */
  config: {
    type: 'project' | 'task' | 'workspace' | 'message' | 'document' | 'user'
  }
  /** The current form mode */
  mode: 'create' | 'edit'
  /** Task ID to exclude from dependencies (for edit mode) */
  excludeTaskId?: string
  /** External data sources - if provided, internal loading is skipped */
  externalWorkspaces?: Workspace[]
  externalProjects?: Project[]
  externalTasks?: Task[]
  externalUsers?: User[]
  /** External selected items - if provided, these override internal selected state */
  externalSelectedProjects?: Project[]
  externalSelectedDependencies?: Task[]
  externalSelectedUsers?: User[]
  /** Initial selected items */
  initialProjects?: Project[]
  initialDependencies?: Task[]
  initialUsers?: User[]
}

interface UseNodeFormDataReturn {
  // Data arrays
  workspaces: Workspace[]
  projects: Project[]
  users: User[]
  tasks: Task[]

  // Selected items state
  selectedProjects: Project[]
  selectedDependencies: Task[]
  selectedUsers: User[]

  // Setters for selected items
  setSelectedProjects: React.Dispatch<React.SetStateAction<Project[]>>
  setSelectedDependencies: React.Dispatch<React.SetStateAction<Task[]>>
  setSelectedUsers: React.Dispatch<React.SetStateAction<User[]>>

  // Effective data (external takes precedence over internal)
  effectiveWorkspaces: Workspace[]
  effectiveProjects: Project[]
  effectiveUsers: User[]
  effectiveTasks: Task[]
  effectiveSelectedProjects: Project[]
  effectiveSelectedDependencies: Task[]
  effectiveSelectedUsers: User[]

  // Loading functions
  loadWorkspaces: () => Promise<void>
  loadTasks: () => Promise<void>
  loadProjectsForWorkspace: (workspaceId: string) => Promise<void>
  loadUsersForWorkspace: (workspaceId: string) => Promise<void>
}

export function useNodeFormData({
  config,
  mode,
  excludeTaskId,
  externalWorkspaces,
  externalProjects,
  externalTasks,
  externalUsers,
  externalSelectedProjects,
  externalSelectedDependencies,
  externalSelectedUsers,
  initialProjects = [],
  initialDependencies = [],
  initialUsers = [],
}: UseNodeFormDataProps): UseNodeFormDataReturn {
  const { user } = useAuth()

  // Internal state for data
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [tasks, setTasks] = useState<Task[]>([])

  // Selected items state
  const [selectedProjects, setSelectedProjects] = useState<Project[]>(
    initialProjects.filter((p): p is Project => p !== null && p !== undefined)
  )
  const [selectedDependencies, setSelectedDependencies] = useState<Task[]>(
    initialDependencies.filter((t): t is Task => t !== null && t !== undefined)
  )
  const [selectedUsers, setSelectedUsers] = useState<User[]>(
    initialUsers.filter((u): u is User => u !== null && u !== undefined)
  )

  // Effective data (external takes precedence over internal)
  const effectiveWorkspaces = externalWorkspaces || workspaces
  const effectiveProjects = externalProjects || projects
  const effectiveUsers = externalUsers || users
  const effectiveTasks = externalTasks || tasks
  const effectiveSelectedProjects = externalSelectedProjects || selectedProjects
  const effectiveSelectedDependencies = externalSelectedDependencies || selectedDependencies
  const effectiveSelectedUsers = externalSelectedUsers || selectedUsers

  // Load workspaces
  const loadWorkspaces = useCallback(async () => {
    if (!user || externalWorkspaces) return

    try {
      const { workspaceService } = await import('@/services')
      const allWorkspaces = await workspaceService.getAll()
      const userWorkspaces = allWorkspaces.filter(
        (workspace) =>
          workspace.creator.id === user.id || workspace.users.some((u) => u.id === user.id)
      )
      setWorkspaces(userWorkspaces)
    } catch (error) {
      console.error('Failed to load workspaces:', error)
      if (mode === 'create') {
        toast.error('Failed to load workspaces')
      }
    }
  }, [user, mode, externalWorkspaces])

  // Load tasks
  const loadTasks = useCallback(async () => {
    if (config.type !== 'task' || externalTasks) return

    try {
      const { taskService } = await import('@/services')
      const allTasks = await taskService.getAll()
      // Filter out the current task from dependencies if in edit mode
      setTasks(excludeTaskId ? allTasks.filter((t) => t.id !== excludeTaskId) : allTasks)
    } catch (error) {
      console.error('Failed to load tasks:', error)
    }
  }, [excludeTaskId, config.type, externalTasks])

  // Load projects for a specific workspace
  const loadProjectsForWorkspace = useCallback(
    async (workspaceId: string) => {
      if (externalProjects) return

      try {
        const { projectService } = await import('@/services')
        const workspaceProjects = await projectService.filter(
          (project) => project.workspace.id === workspaceId
        )
        setProjects(workspaceProjects)
      } catch (error) {
        console.error('Failed to load projects:', error)
      }
    },
    [externalProjects]
  )

  // Load users for a specific workspace
  const loadUsersForWorkspace = useCallback(
    async (workspaceId: string) => {
      if (externalUsers) return

      try {
        const { workspaceService } = await import('@/services')
        const workspace = await workspaceService.getById(workspaceId)
        if (workspace) {
          setUsers(workspace.users)
        }
      } catch (error) {
        console.error('Failed to load users:', error)
      }
    },
    [externalUsers]
  )

  // Load initial data
  useEffect(() => {
    loadWorkspaces()
    loadTasks()
  }, [loadWorkspaces, loadTasks])

  return {
    // Data arrays
    workspaces,
    projects,
    users,
    tasks,

    // Selected items state
    selectedProjects,
    selectedDependencies,
    selectedUsers,

    // Setters for selected items
    setSelectedProjects,
    setSelectedDependencies,
    setSelectedUsers,

    // Effective data
    effectiveWorkspaces,
    effectiveProjects,
    effectiveUsers,
    effectiveTasks,
    effectiveSelectedProjects,
    effectiveSelectedDependencies,
    effectiveSelectedUsers,

    // Loading functions
    loadWorkspaces,
    loadTasks,
    loadProjectsForWorkspace,
    loadUsersForWorkspace,
  }
}
